/**
 * Hand-rolled SVG bar chart for throughput (tasks completed per interval).
 * Mirrors the CycleBurndown SVG approach: plain viewBox + CSS var colours.
 */
import type { ThroughputBucket } from "../../lib/api";
import { computeBarLayout, linearScale } from "./chartHelpers";

interface Props {
  buckets: ThroughputBucket[];
  total: number;
}

const W = 320;
const H = 120;
const PAD_LEFT = 36;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 24;
const PLOT_X = PAD_LEFT;
const PLOT_W = W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = H - PAD_TOP - PAD_BOTTOM;
const PLOT_Y_BOTTOM = PAD_TOP + PLOT_H;

export function ThroughputChart({ buckets, total }: Props): preact.JSX.Element {
  if (buckets.length === 0 || total === 0) {
    return (
      <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
        Keine Daten im gewählten Zeitraum
      </p>
    );
  }

  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const { barWidth, centres } = computeBarLayout(buckets.length, PLOT_X, PLOT_W);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: "auto" }}
      aria-label="Durchsatz-Diagramm"
    >
      {/* Y axis baseline */}
      <line
        x1={PLOT_X}
        y1={PLOT_Y_BOTTOM}
        x2={PLOT_X + PLOT_W}
        y2={PLOT_Y_BOTTOM}
        stroke="currentColor"
        stroke-width={0.5}
        opacity={0.3}
      />
      {/* Y max label */}
      <text
        x={PLOT_X - 3}
        y={PAD_TOP}
        text-anchor="end"
        dominant-baseline="middle"
        font-size="9"
        fill="currentColor"
        opacity={0.6}
      >
        {maxCount}
      </text>
      {/* 0 label */}
      <text
        x={PLOT_X - 3}
        y={PLOT_Y_BOTTOM}
        text-anchor="end"
        dominant-baseline="middle"
        font-size="9"
        fill="currentColor"
        opacity={0.6}
      >
        0
      </text>

      {/* Bars */}
      {buckets.map((b, i) => {
        const cx = centres[i]!;
        const barH = Math.max(
          1,
          linearScale(b.count, 0, maxCount, 0, PLOT_H)
        );
        const x = cx - barWidth / 2;
        const y = PLOT_Y_BOTTOM - barH;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barH}
              rx={2}
              fill="var(--color-primary)"
              opacity={0.85}
            >
              <title>{`${b.label}: ${b.count}`}</title>
            </rect>
            {/* X label — only show first and last to avoid crowding */}
            {(i === 0 || i === buckets.length - 1) && (
              <text
                x={cx}
                y={H - PAD_BOTTOM + 13}
                text-anchor="middle"
                font-size="8"
                fill="currentColor"
                opacity={0.55}
              >
                {/* Show a short slice of the label */}
                {b.label.length > 8 ? b.label.slice(5) : b.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
